import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Surface,
  FAB,
} from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';
import {
  ProgressInsightsPanel,
  type AIProgressInsight,
  type SkillGapAnalysis,
  type LearningEfficiencyMetrics,
} from './ProgressInsightsPanel';
import {
  SkillGapAnalyzer,
  type SkillGap,
  type SkillDependency,
} from './SkillGapAnalyzer';
import {
  LearningEfficiencyTracker,
  type LearningEfficiencyData,
} from './LearningEfficiencyTracker';
import {
  ProgressTrendAnalyzer,
  type ProgressTrendData,
} from './ProgressTrendAnalyzer';

import { logger } from '../../utils/logger';
import {
  ProgressSharingPanel,
  type ProgressShareData,
  type PrivacySettings,
  type ShareTemplate,
} from './ProgressSharingPanel';

interface ProgressInsightsHubProps {
  masteryStates: MasteryState[];
  userId: string;
  onInsightAction: (insightId: string, actionType: string) => void;
  onSkillFocus: (skillId: string) => void;
  onRecommendationSelect: (recommendation: any) => void;
  onShareProgress: (shareData: any) => void;
  style?: any;
}

type HubMode =
  | 'overview'
  | 'insights'
  | 'gaps'
  | 'efficiency'
  | 'trends'
  | 'sharing';

export function ProgressInsightsHub({
  masteryStates,
  userId,
  onInsightAction,
  onSkillFocus,
  onRecommendationSelect,
  onShareProgress,
  style,
}: ProgressInsightsHubProps) {
  const theme = useTheme();
  const [hubMode, setHubMode] = useState<HubMode>('overview');
  const [isLoading, setIsLoading] = useState(false);

  // Mock data - in real implementation, this would come from API calls
  const [insights, setInsights] = useState<AIProgressInsight[]>([
    {
      id: 'insight_1',
      type: 'improvement',
      title: 'Optimize Your Morning Learning',
      description:
        'Your retention rate is 23% higher when learning between 8-10 AM',
      aiReasoning:
        'Analysis of your learning sessions shows consistent patterns of better performance and retention during morning hours. Your focus metrics and completion rates are significantly higher during this time window.',
      confidence: 0.87,
      impact: 'high',
      priority: 1,
      actionable: true,
      recommendations: [
        {
          id: 'rec_1',
          type: 'schedule',
          title: 'Schedule Daily Morning Sessions',
          description: 'Block 30 minutes each morning for focused learning',
          estimatedImpact: 0.23,
          timeRequired: 30,
          difficulty: 0.3,
        },
      ],
      relatedSkills: ['espresso-basics', 'milk-steaming'],
      generatedAt: new Date().toISOString(),
    },
    {
      id: 'insight_2',
      type: 'skill_gap',
      title: 'Latte Art Blocking Advanced Skills',
      description:
        'Improving latte art skills will unlock 3 advanced presentation techniques',
      aiReasoning:
        'Your current latte art mastery (45%) is preventing progression in advanced presentation skills. The dependency analysis shows this is a critical bottleneck.',
      confidence: 0.92,
      impact: 'high',
      priority: 2,
      actionable: true,
      recommendations: [
        {
          id: 'rec_2',
          type: 'practice',
          title: 'Daily Latte Art Practice',
          description:
            'Focus on basic heart and leaf patterns for 15 minutes daily',
          estimatedImpact: 0.35,
          timeRequired: 15,
          difficulty: 0.6,
        },
      ],
      relatedSkills: ['latte-art', 'milk-steaming', 'presentation'],
      generatedAt: new Date().toISOString(),
    },
  ]);

  const [skillGaps, setSkillGaps] = useState<SkillGap[]>([
    {
      skillId: 'latte-art',
      skillName: 'Latte Art',
      category: 'Presentation',
      currentLevel: 0.45,
      targetLevel: 0.8,
      gap: 0.35,
      priority: 'high',
      blockedSkills: [
        'advanced-presentation',
        'competition-techniques',
        'artistic-variations',
      ],
      prerequisites: ['milk-steaming', 'espresso-basics'],
      estimatedTimeToClose: 1200, // 20 hours
      difficulty: 0.7,
      aiRecommendations: [
        {
          id: 'gap_rec_1',
          type: 'practice',
          title: 'Structured Latte Art Course',
          description:
            'Follow a progressive curriculum from basic shapes to complex designs',
          estimatedImpact: 0.4,
          timeRequired: 45,
          difficulty: 0.6,
          confidence: 0.85,
          reasoning:
            'Structured learning with immediate feedback will accelerate skill development',
        },
      ],
      learningPath: [
        {
          id: 'step_1',
          title: 'Master Basic Milk Steaming',
          description: 'Perfect microfoam consistency and temperature control',
          type: 'practice',
          estimatedTime: 300,
          prerequisites: [],
          skills: ['milk-steaming'],
          resources: [],
        },
        {
          id: 'step_2',
          title: 'Learn Heart Pattern',
          description: 'Practice basic heart shape with consistent results',
          type: 'practice',
          estimatedTime: 480,
          prerequisites: ['step_1'],
          skills: ['latte-art'],
          resources: [],
        },
      ],
    },
  ]);

  const [efficiencyData, setEfficiencyData] = useState<LearningEfficiencyData>({
    userId,
    timeRange: '30d',
    overallEfficiency: 0.73,
    metrics: [
      {
        id: 'time_optimization',
        name: 'Time Optimization',
        category: 'time',
        value: 0.68,
        trend: 'improving',
        change: 12.5,
        benchmark: 0.62,
        description: 'How effectively you use your learning time',
        aiInsights: [
          'Morning sessions are 23% more effective',
          'Short breaks improve focus by 15%',
        ],
      },
      {
        id: 'engagement',
        name: 'Content Engagement',
        category: 'engagement',
        value: 0.82,
        trend: 'stable',
        change: 2.1,
        benchmark: 0.75,
        description: 'Your level of interaction with learning materials',
        aiInsights: [
          'Video content shows highest engagement',
          'Interactive exercises boost retention',
        ],
      },
    ],
    trends: [],
    optimizations: [
      {
        id: 'opt_1',
        category: 'schedule',
        title: 'Optimize Learning Schedule',
        description:
          'Shift primary learning sessions to morning hours for better retention',
        potentialImprovement: 0.23,
        implementationDifficulty: 'easy',
        timeToSeeResults: 7,
        confidence: 0.87,
        aiReasoning:
          'Your cognitive performance peaks between 8-10 AM based on completion rates and retention tests',
        actionSteps: [
          {
            id: 'step_1',
            title: 'Set Morning Learning Block',
            description: 'Reserve 8:30-9:30 AM for focused learning sessions',
            estimatedTime: 5,
            priority: 1,
          },
        ],
        relatedMetrics: ['time_optimization', 'engagement'],
      },
    ],
    patterns: [
      {
        id: 'pattern_1',
        type: 'temporal',
        name: 'Morning Peak Performance',
        description:
          'Consistently higher performance and retention during morning hours',
        strength: 0.85,
        impact: 0.23,
        examples: [
          '8 AM sessions: 92% completion',
          '2 PM sessions: 67% completion',
        ],
        recommendations: [
          'Schedule challenging content in the morning',
          'Use afternoons for review',
        ],
      },
    ],
    predictions: [],
    lastUpdated: new Date().toISOString(),
  });

  const [trendData, setTrendData] = useState<ProgressTrendData>({
    userId,
    timeRange: '30d',
    overallTrend: {
      direction: 'upward',
      velocity: 0.02, // 2% per day
      acceleration: 0.001,
      confidence: 0.85,
      r2Score: 0.78,
      seasonality: null,
      breakpoints: [],
    },
    skillTrends: masteryStates.map(mastery => ({
      skillId: mastery.skillId,
      skillName: mastery.skillId
        .replace('-', ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
      category: mastery.skillCategory || 'General',
      trend: {
        direction:
          mastery.trend === 'improving'
            ? 'upward'
            : mastery.trend === 'declining'
              ? 'downward'
              : 'stable',
        velocity: Math.random() * 0.05,
        acceleration: (Math.random() - 0.5) * 0.002,
        confidence: 0.7 + Math.random() * 0.3,
        r2Score: 0.6 + Math.random() * 0.4,
        seasonality: null,
        breakpoints: [],
      },
      dataPoints: [],
      correlations: [],
      forecast: [],
    })),
    categoryTrends: [],
    predictions: [
      {
        type: 'milestone',
        description: 'Espresso Mastery Achievement',
        predictedDate: new Date(
          Date.now() + 14 * 24 * 60 * 60 * 1000
        ).toISOString(),
        confidence: 0.82,
        probability: 0.78,
        conditions: [
          'Maintain current practice schedule',
          'Complete advanced techniques module',
        ],
        recommendations: [
          'Focus on consistency over speed',
          'Practice daily for 20 minutes',
        ],
      },
    ],
    milestones: [],
    patterns: [],
    anomalies: [],
    lastUpdated: new Date().toISOString(),
  });

  const [shareHistory, setShareHistory] = useState<ProgressShareData[]>([]);
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    defaultVisibility: 'friends',
    allowAnonymousSharing: true,
    includePersonalData: false,
    allowComments: true,
    allowReactions: true,
    autoExpireShares: true,
    expireDays: 30,
    notifyOnInteraction: true,
    shareAnalytics: true,
  });

  const shareTemplates: ShareTemplate[] = [
    {
      id: 'milestone',
      name: 'Milestone Achievement',
      description: 'Share a major learning milestone',
      type: 'milestone',
      defaultInclusion: {
        overallProgress: true,
        skillBreakdown: false,
        timeSpent: false,
        achievements: true,
        personalInsights: false,
        comparisons: false,
        predictions: false,
        customMessage: true,
      },
      customizable: true,
      preview: 'Just reached 80% mastery in Espresso Basics! 🚀',
    },
    {
      id: 'journey',
      name: 'Learning Journey',
      description: 'Share your overall progress story',
      type: 'journey',
      defaultInclusion: {
        overallProgress: true,
        skillBreakdown: true,
        timeSpent: true,
        achievements: true,
        personalInsights: true,
        comparisons: false,
        predictions: true,
        customMessage: true,
      },
      customizable: true,
      preview: 'My coffee learning journey: 15 skills mastered in 3 months!',
    },
  ];

  // Calculate overview stats
  const overviewStats = React.useMemo(() => {
    const totalInsights = insights.length;
    const actionableInsights = insights.filter(i => i.actionable).length;
    const highImpactInsights = insights.filter(i => i.impact === 'high').length;
    const criticalGaps = skillGaps.filter(
      g => g.priority === 'critical' || g.priority === 'high'
    ).length;
    const efficiencyScore = Math.round(efficiencyData.overallEfficiency * 100);

    return {
      totalInsights,
      actionableInsights,
      highImpactInsights,
      criticalGaps,
      efficiencyScore,
    };
  }, [insights, skillGaps, efficiencyData]);

  const handleCreateShare = useCallback(
    (shareData: Partial<ProgressShareData>) => {
      const newShare: ProgressShareData = {
        ...shareData,
        shareId: shareData.shareId || `share_${Date.now()}`,
        userId,
        analytics: {
          views: 0,
          interactions: 0,
          shares: 0,
          comments: 0,
          reactions: [],
        },
      } as ProgressShareData;

      setShareHistory(prev => [newShare, ...prev]);
      onShareProgress(newShare);
    },
    [userId, onShareProgress]
  );

  const renderOverview = () => (
    <ScrollView style={styles.overviewContainer}>
      {/* Quick Stats */}
      <Card style={styles.statsCard}>
        <Text variant='titleLarge' style={styles.statsTitle}>
          Progress Insights Overview
        </Text>
        <View style={styles.statsGrid}>
          <TouchableOpacity
            style={styles.statItem}
            onPress={() => setHubMode('insights')}
          >
            <Text variant='headlineSmall' style={styles.statValue}>
              {overviewStats.totalInsights}
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              AI Insights
            </Text>
            <Text variant='bodySmall' style={styles.statSubLabel}>
              {overviewStats.actionableInsights} actionable
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.statItem}
            onPress={() => setHubMode('gaps')}
          >
            <Text
              variant='headlineSmall'
              style={[
                styles.statValue,
                {
                  color: overviewStats.criticalGaps > 0 ? '#F57C00' : '#4CAF50',
                },
              ]}
            >
              {overviewStats.criticalGaps}
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              Critical Gaps
            </Text>
            <Text variant='bodySmall' style={styles.statSubLabel}>
              Need attention
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.statItem}
            onPress={() => setHubMode('efficiency')}
          >
            <Text
              variant='headlineSmall'
              style={[
                styles.statValue,
                {
                  color:
                    overviewStats.efficiencyScore >= 70 ? '#4CAF50' : '#FF9800',
                },
              ]}
            >
              {overviewStats.efficiencyScore}%
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              Efficiency
            </Text>
            <Text variant='bodySmall' style={styles.statSubLabel}>
              Learning rate
            </Text>
          </TouchableOpacity>
        </View>
      </Card>

      {/* Quick Actions */}
      <Card style={styles.actionsCard}>
        <Text variant='titleMedium' style={styles.actionsTitle}>
          Quick Actions
        </Text>
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => setHubMode('insights')}
          >
            <IconButton icon='lightbulb' size={24} />
            <Text variant='labelMedium'>View Insights</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => setHubMode('gaps')}
          >
            <IconButton icon='target' size={24} />
            <Text variant='labelMedium'>Analyze Gaps</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => setHubMode('trends')}
          >
            <IconButton icon='chart-line' size={24} />
            <Text variant='labelMedium'>View Trends</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => setHubMode('sharing')}
          >
            <IconButton icon='share' size={24} />
            <Text variant='labelMedium'>Share Progress</Text>
          </TouchableOpacity>
        </View>
      </Card>

      {/* Recent Highlights */}
      <Card style={styles.highlightsCard}>
        <Text variant='titleMedium' style={styles.highlightsTitle}>
          Recent Highlights
        </Text>

        {/* Top Insight */}
        {insights.length > 0 && (
          <TouchableOpacity
            style={styles.highlightItem}
            onPress={() => setHubMode('insights')}
          >
            <IconButton icon='lightbulb' size={20} iconColor='#FFC107' />
            <View style={styles.highlightContent}>
              <Text variant='titleSmall' style={styles.highlightTitle}>
                {insights[0].title}
              </Text>
              <Text variant='bodySmall' style={styles.highlightDescription}>
                {insights[0].description}
              </Text>
            </View>
            <IconButton icon='chevron-right' size={16} />
          </TouchableOpacity>
        )}

        {/* Top Gap */}
        {skillGaps.length > 0 && (
          <TouchableOpacity
            style={styles.highlightItem}
            onPress={() => setHubMode('gaps')}
          >
            <IconButton icon='target' size={20} iconColor='#F57C00' />
            <View style={styles.highlightContent}>
              <Text variant='titleSmall' style={styles.highlightTitle}>
                Focus on {skillGaps[0].skillName}
              </Text>
              <Text variant='bodySmall' style={styles.highlightDescription}>
                {Math.round(skillGaps[0].gap * 100)}% gap to target level
              </Text>
            </View>
            <IconButton icon='chevron-right' size={16} />
          </TouchableOpacity>
        )}

        {/* Efficiency Tip */}
        {efficiencyData.optimizations.length > 0 && (
          <TouchableOpacity
            style={styles.highlightItem}
            onPress={() => setHubMode('efficiency')}
          >
            <IconButton icon='speedometer' size={20} iconColor='#4CAF50' />
            <View style={styles.highlightContent}>
              <Text variant='titleSmall' style={styles.highlightTitle}>
                {efficiencyData.optimizations[0].title}
              </Text>
              <Text variant='bodySmall' style={styles.highlightDescription}>
                +
                {Math.round(
                  efficiencyData.optimizations[0].potentialImprovement * 100
                )}
                % potential improvement
              </Text>
            </View>
            <IconButton icon='chevron-right' size={16} />
          </TouchableOpacity>
        )}
      </Card>
    </ScrollView>
  );

  const renderContent = () => {
    switch (hubMode) {
      case 'overview':
        return renderOverview();

      case 'insights':
        return (
          <ProgressInsightsPanel
            insights={insights}
            skillGaps={skillGaps}
            efficiencyMetrics={{
              overallEfficiency: efficiencyData.overallEfficiency,
              timeOptimization:
                efficiencyData.metrics.find(m => m.id === 'time_optimization')
                  ?.value || 0,
              contentEngagement:
                efficiencyData.metrics.find(m => m.id === 'engagement')
                  ?.value || 0,
              retentionRate: 0.75,
              practiceEffectiveness: 0.68,
              suggestions: efficiencyData.optimizations,
            }}
            masteryStates={masteryStates}
            onInsightAction={onInsightAction}
            onRecommendationSelect={onRecommendationSelect}
            onSkillGapFocus={onSkillFocus}
          />
        );

      case 'gaps':
        return (
          <SkillGapAnalyzer
            masteryStates={masteryStates}
            skillGaps={skillGaps}
            skillDependencies={[]}
            targetSkillLevels={{}}
            onGapFocus={onSkillFocus}
            onRecommendationSelect={onRecommendationSelect}
            onLearningPathStart={(skillId, path) => {
              logger.log('Starting learning path for', skillId, path);
            }}
          />
        );

      case 'efficiency':
        return (
          <LearningEfficiencyTracker
            efficiencyData={efficiencyData}
            onOptimizationApply={suggestion => {
              logger.log('Applying optimization:', suggestion);
            }}
            onPatternExplore={pattern => {
              logger.log('Exploring pattern:', pattern);
            }}
            onMetricDrillDown={metricId => {
              logger.log('Drilling down on metric:', metricId);
            }}
          />
        );

      case 'trends':
        return (
          <ProgressTrendAnalyzer
            trendData={trendData}
            masteryStates={masteryStates}
            onSkillFocus={onSkillFocus}
            onPredictionExplore={prediction => {
              logger.log('Exploring prediction:', prediction);
            }}
            onAnomalyInvestigate={anomaly => {
              logger.log('Investigating anomaly:', anomaly);
            }}
          />
        );

      case 'sharing':
        return (
          <ProgressSharingPanel
            masteryStates={masteryStates}
            shareHistory={shareHistory}
            privacySettings={privacySettings}
            shareTemplates={shareTemplates}
            onCreateShare={handleCreateShare}
            onUpdatePrivacy={setPrivacySettings}
            onDeleteShare={shareId => {
              setShareHistory(prev => prev.filter(s => s.shareId !== shareId));
            }}
            onViewShare={shareId => {
              logger.log('Viewing share:', shareId);
            }}
          />
        );

      default:
        return renderOverview();
    }
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            onPress={() => setHubMode('overview')}
            style={styles.headerTitle}
          >
            <Text variant='headlineSmall' style={styles.title}>
              Progress Insights
            </Text>
            {hubMode !== 'overview' && <IconButton icon='home' size={20} />}
          </TouchableOpacity>
        </View>

        {/* Mode Indicator */}
        {hubMode !== 'overview' && (
          <View style={styles.modeIndicator}>
            <Chip
              icon={
                hubMode === 'insights'
                  ? 'lightbulb'
                  : hubMode === 'gaps'
                    ? 'target'
                    : hubMode === 'efficiency'
                      ? 'speedometer'
                      : hubMode === 'trends'
                        ? 'chart-line'
                        : hubMode === 'sharing'
                          ? 'share'
                          : 'view-dashboard'
              }
              style={styles.modeChip}
            >
              {hubMode.charAt(0).toUpperCase() + hubMode.slice(1)}
            </Chip>
          </View>
        )}
      </View>

      {/* Content */}
      <View style={styles.content}>{renderContent()}</View>

      {/* Floating Action Button */}
      {hubMode === 'overview' && (
        <FAB
          icon='share'
          style={styles.fab}
          onPress={() => setHubMode('sharing')}
          label='Share Progress'
        />
      )}
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  title: {
    fontWeight: '600',
    flex: 1,
  },
  modeIndicator: {
    marginTop: 8,
  },
  modeChip: {
    alignSelf: 'flex-start',
  },
  content: {
    flex: 1,
  },

  // Overview Styles
  overviewContainer: {
    flex: 1,
    padding: 16,
  },
  statsCard: {
    padding: 16,
    marginBottom: 16,
  },
  statsTitle: {
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statValue: {
    fontWeight: 'bold',
    color: '#6750A4',
  },
  statLabel: {
    opacity: 0.7,
    marginTop: 4,
    textAlign: 'center',
  },
  statSubLabel: {
    opacity: 0.5,
    marginTop: 2,
    fontSize: 10,
    textAlign: 'center',
  },
  actionsCard: {
    padding: 16,
    marginBottom: 16,
  },
  actionsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  actionButton: {
    alignItems: 'center',
    flex: 1,
  },
  highlightsCard: {
    padding: 16,
  },
  highlightsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  highlightItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  highlightContent: {
    flex: 1,
    marginLeft: 8,
  },
  highlightTitle: {
    fontWeight: '600',
  },
  highlightDescription: {
    opacity: 0.7,
    marginTop: 2,
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
});
